/**
 * ░░░░░▄▀▀▀▄░░░░░░░░░░░░░░░░░
 * ▄███▀░◐░░░▌░░░░░░░░░░░░░░░░
 * ░░░░▌░░░░░▐░░░░░░░░░░░░░░░░
 * ░░░░▐░░░░░▐░░░░░░░░░░░░░░░░
 * ░░░░▌░░░░░▐▄▄░░░░░░░░░░░░░░
 * ░░░░▌░░░░▄▀▒▒▀▀▀▀▄░░░░░░░░░
 * ░░░▐░░░░▐▒▒▒▒▒▒▒▒▀▀▄░░░░░░░
 * ░░░▐░░░░▐▄▒▒▒▒▒▒▒▒▒▒▀▄░░░░░
 * ░░░░▀▄░░░░▀▄▒▒▒▒▒▒▒▒▒▒▀▄░░░
 * ░░░░░░▀▄▄▄▄▄█▄▄▄▄▄▄▄▄▄▄▄▀▄░
 * ░░░░░░░░░░░▌▌░▌▌░░░░░░░░░░░
 * ░░░░░░░░░░░▌▌░▌▌░░░░░░░░░░░
 * ░░░░░░░░░▄▄▌▌▄▌▌░░░░░░░░░░░
 *
 * MIGRATION NOTICE
 *
 * We are in the process of re-writing our components using function components
 * and React Hooks, rather than extending Component.
 *
 * We have found that hooks make our components better, and - once you get the
 * hang of them - produce fewer bugs and edge cases than conventional lifecycle
 * methods (like componentDidMount and getDerivedStateFromProps).
 *
 * However, hooks can’t be used from class components. Therefore, we are trying
 * to eliminate class components wherever we can.
 *
 * If you’re changing this file, please consider contributing to this effort by
 * changing this component to be a function that uses hooks, and then removing
 * this notice.
 */
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { css, cx } from 'emotion';

import GlobalEvents from 'site-react/core/Events/GlobalEvents';
import AccountIcon from 'site-react/shared/AccountIcon';
import theme from 'site-react/theme';

import AccountLinks from '../data/account';
import DesktopNavMenu from './DesktopNavMenu';

const accountIconInnerSpacing = 5;

const accountIconStyles = css`
  background: none;
  display: block;
  margin-left: ${theme.spacing.xl - accountIconInnerSpacing}px;
`;

const dropdownStyles = css`
  background-color: white;
  border-radius: 4px;
  border: 1px solid ${theme.colors.neutral100};
  box-shadow: ${theme.shadow.resting};
  display: none;
  padding: ${theme.spacing.base * 7}px;
  position: absolute;
  right: 0;
  top: 100%;
`;

const dropdownOpenStyles = css`
  display: block;
`;

class DesktopNavMenuAccount extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isDropdownOpen: false,
    };

    this.globalEvents = new GlobalEvents({
      click: this.onAnyClick.bind(this),
    });

    this.onToggleDropdown = this.onToggleDropdown.bind(this);
  }

  componentDidUpdate() {
    const { isDropdownOpen } = this.state;
    // Ensures that we only handle global events when
    // the dropdown is open
    if (isDropdownOpen) {
      this.globalEvents.listen();
    } else {
      this.globalEvents.remove();
    }
  }

  onAnyClick(event) {
    if (!event.target.closest('#nav-account-dropdown')) {
      this.closeDropdown();
    }
  }

  onToggleDropdown() {
    this.setState((prevState) => {
      return {
        isDropdownOpen: !prevState.isDropdownOpen,
      };
    });
  }

  closeDropdown() {
    this.setState({ isDropdownOpen: false });
  }

  render() {
    const { isLoggedIn, user } = this.props;
    const { isDropdownOpen } = this.state;

    return (
      <>
        <DesktopNavMenu
          links={isLoggedIn ? AccountLinks.loggedIn : AccountLinks.loggedOut}
          role="navigation"
          {...this.props}
        />
        {isLoggedIn && user && (
          <div id="nav-account-dropdown">
            <AccountIcon
              className={accountIconStyles}
              hover
              imgSrc={user.avatar}
              name={user.name}
              size={40}
              onClick={this.onToggleDropdown}
            />
            <div
              className={cx(dropdownStyles, {
                [dropdownOpenStyles]: isDropdownOpen,
              })}
            >
              <DesktopNavMenu
                isVertical
                links={AccountLinks.loggedInAccount}
                role="navigation"
                {...this.props}
              />
            </div>
          </div>
        )}
      </>
    );
  }
}

DesktopNavMenuAccount.defaultProps = {
  onNavListItemClick: null,
  user: {
    avatar: '',
    name: '',
  },
};

DesktopNavMenuAccount.propTypes = {
  /**
   * Is user logged in
   */
  isLoggedIn: PropTypes.bool.isRequired,
  /**
   * Method run on menu item click
   */
  onNavListItemClick: PropTypes.func,
  /**
   * User information
   */
  user: PropTypes.shape({
    avatar: PropTypes.string,
    name: PropTypes.string,
  }),
};

export default DesktopNavMenuAccount;
